"use client";

import { useEffect, useRef, useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, LABEL } from "@/components/tool-kit/styles";
import {
  type CalendarStyle,
  MonthArt,
  YearArt,
} from "@/components/tools/printables/calendar-art";
import {
  PaperControls,
  PRINTABLES_COMMON_STRINGS,
  PrintablePages,
  PrintBar,
  useTextMeasure,
} from "@/components/tools/printables/page-kit";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  clampYear,
  monthName,
  type WeekStart,
} from "@/lib/tool-kit/printables/calendar";
import {
  layoutMonthPage,
  layoutYearPage,
} from "@/lib/tool-kit/printables/calendar-pages";
import { ACCENTS, type AccentKey } from "@/lib/tool-kit/printables/certificate";
import {
  holidayMap,
  legendEntries,
  parseHolidays,
} from "@/lib/tool-kit/printables/holidays";
import {
  type Orientation,
  type PaperSize,
  pageGeometry,
} from "@/lib/tool-kit/printables/page";

/** English strings. A translated content file overrides these through `ui`. */
export const PRINTABLE_CALENDAR_GENERATOR_STRINGS = {
  ...PRINTABLES_COMMON_STRINGS,
  layoutLabel: "Layout",
  layouts: {
    year: "Year at a glance — one page",
    months: "One month per page",
  },
  yearLabel: "Year",
  firstMonthLabel: "First month",
  quickLabel: "Quick start",
  quick: {
    calendar: "Calendar year",
    august: "Academic year from August",
    september: "Academic year from September",
  },
  monthsCountLabel: "Months to print",
  weekStartLabel: "Week starts on",
  weekStarts: { sunday: "Sunday", monday: "Monday" },
  weekNumbers: "ISO week numbers",
  weekNumbersHint:
    "Weeks start on Monday in ISO 8601. With a Sunday start, each row shows the week number of its Thursday.",
  weekHead: "Wk",
  shadeWeekends: "Shade weekends",
  showOutside: "Show days of the neighbouring months",
  notes: "Notes area under each month",
  notesLabel: "Notes",
  titleLabel: "Your title (optional)",
  titleHint: "Printed in the corner, for example a class or a room.",
  holidaysLabel: "Holidays and days off — one per line",
  holidaysHint:
    "Nothing is added for you. Write 2026-12-25 Winter holiday for one day, 2026-12-21..2027-01-01 Winter break for a range, or 12-25 Christmas Day for every year.",
  holidaysPlaceholder:
    "2026-12-21..2027-01-01 Winter break\n12-25 Christmas Day",
  holidayErrors: "These lines could not be read: {lines}",
  moreHolidays: "… and more",
  accentLabel: "Colour",
  accents: {
    navy: "Navy",
    burgundy: "Burgundy",
    forest: "Forest green",
    gold: "Antique gold",
    purple: "Purple",
    black: "Black and white",
  },
  previewLabel: "Calendar preview",
} as const;

type LayoutKey = keyof typeof PRINTABLE_CALENDAR_GENERATOR_STRINGS.layouts;
type QuickKey = keyof typeof PRINTABLE_CALENDAR_GENERATOR_STRINGS.quick;

const DEFAULT_YEAR = 2026;
const MONTH_CHOICES = Array.from({ length: 12 }, (_, i) => String(i + 1));
const QUICK_START: Record<QuickKey, number> = {
  calendar: 0,
  august: 7,
  september: 8,
};

export default function PrintableCalendarGeneratorWidget() {
  const strings = useToolUi(PRINTABLE_CALENDAR_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const measure = useTextMeasure();
  const svgRef = useRef<SVGSVGElement>(null);
  const [layout, setLayout] = useState<LayoutKey>("year");
  const [year, setYear] = useState(DEFAULT_YEAR);
  const [yearText, setYearText] = useState(String(DEFAULT_YEAR));
  const [firstMonth, setFirstMonth] = useState(0);
  const [monthsCount, setMonthsCount] = useState("12");
  const [weekStart, setWeekStart] = useState<WeekStart>(0);
  const [weekNumbers, setWeekNumbers] = useState(false);
  const [shadeWeekends, setShadeWeekends] = useState(true);
  const [showOutside, setShowOutside] = useState(true);
  const [notes, setNotes] = useState(false);
  const [title, setTitle] = useState("");
  const [holidayText, setHolidayText] = useState("");
  const [accent, setAccent] = useState<AccentKey>("navy");
  const [paper, setPaper] = useState<PaperSize>("letter");
  const [orientation, setOrientation] = useState<Orientation>("portrait");

  useEffect(() => {
    const now = new Date().getFullYear();
    setYear(now);
    setYearText(String(now));
  }, []);

  function chooseLayout(next: LayoutKey) {
    setLayout(next);
    setOrientation(next === "months" ? "landscape" : "portrait");
  }

  function editYear(text: string) {
    setYearText(text);
    const value = parseIntegerInput(text);
    if (value !== null && value >= 1900 && value <= 2199) {
      setYear(clampYear(value));
    }
  }

  const geometry = pageGeometry(paper, orientation);
  const parsed = parseHolidays(holidayText);
  const map = holidayMap(parsed.holidays, [year - 1, year, year + 1, year + 2]);
  const style: CalendarStyle = {
    locale,
    weekStart,
    accent: ACCENTS[accent],
    shadeWeekends,
    showOutside,
    holidays: map,
  };
  const right = geometry.left + geometry.contentWidth;
  const pages: React.ReactNode[] = [];
  if (layout === "months") {
    const count = Math.min(12, Math.max(1, Number(monthsCount)));
    for (let i = 0; i < count; i += 1) {
      const index = firstMonth + i;
      const page = layoutMonthPage(
        geometry,
        year + Math.floor(index / 12),
        index % 12,
        weekStart,
        { weekNumbers, notes }
      );
      pages.push(
        <MonthArt
          key={`${page.year}-${page.month}`}
          left={geometry.left}
          measure={measure}
          notesLabel={strings.notesLabel}
          page={page}
          right={right}
          style={style}
          title={title.trim()}
          weekLabel={strings.weekHead}
        />
      );
    }
  } else {
    const hasHolidays = parsed.holidays.length > 0;
    const page = layoutYearPage(geometry, year, firstMonth, weekStart, {
      weekNumbers,
      legend: hasHolidays,
      columns: orientation === "landscape" ? 4 : 3,
    });
    const months = page.months.map((mini) => ({
      year: mini.year,
      month: mini.month,
    }));
    pages.push(
      <YearArt
        heading={firstMonth === 0 ? String(year) : `${year}–${year + 1}`}
        key="year"
        left={geometry.left}
        legend={legendEntries(map, months)}
        legendMore={strings.moreHolidays}
        measure={measure}
        page={page}
        right={right}
        showYearInNames={firstMonth !== 0}
        style={style}
        title={title.trim()}
      />
    );
  }

  return (
    <div className="space-y-6 print:m-0 print:space-y-0">
      <div className="space-y-5 print:hidden">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <SelectField
            id="pc-layout"
            label={strings.layoutLabel}
            onChange={chooseLayout}
            options={(["year", "months"] as const).map((value) => ({
              value,
              label: strings.layouts[value],
            }))}
            value={layout}
          />
          <TextField
            id="pc-year"
            label={strings.yearLabel}
            numeric
            onChange={editYear}
            value={yearText}
          />
          <SelectField
            id="pc-first-month"
            label={strings.firstMonthLabel}
            onChange={(value) => setFirstMonth(Number(value) - 1)}
            options={MONTH_CHOICES.map((value) => ({
              value,
              label: monthName(locale, Number(value) - 1),
            }))}
            value={String(firstMonth + 1)}
          />
          {layout === "months" ? (
            <SelectField
              id="pc-count"
              label={strings.monthsCountLabel}
              onChange={setMonthsCount}
              options={MONTH_CHOICES.map((value) => ({ value, label: value }))}
              value={monthsCount}
            />
          ) : null}
          <SelectField
            id="pc-week-start"
            label={strings.weekStartLabel}
            onChange={(value) => setWeekStart(value === "monday" ? 1 : 0)}
            options={(["sunday", "monday"] as const).map((value) => ({
              value,
              label: strings.weekStarts[value],
            }))}
            value={weekStart === 1 ? "monday" : "sunday"}
          />
          <SelectField
            id="pc-accent"
            label={strings.accentLabel}
            onChange={setAccent}
            options={(Object.keys(ACCENTS) as AccentKey[]).map((value) => ({
              value,
              label: strings.accents[value],
            }))}
            value={accent}
          />
          <PaperControls
            idPrefix="pc"
            onOrientation={setOrientation}
            onPaper={setPaper}
            orientation={orientation}
            paper={paper}
            strings={strings}
          />
        </div>
        <div className="space-y-2">
          <p className={LABEL}>{strings.quickLabel}</p>
          <div className="flex flex-wrap gap-2">
            {(Object.keys(QUICK_START) as QuickKey[]).map((key) => (
              <button
                className={BUTTON_SMALL}
                key={key}
                onClick={() => {
                  setFirstMonth(QUICK_START[key]);
                  setMonthsCount("12");
                }}
                type="button"
              >
                {strings.quick[key]}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Checkbox
            checked={weekNumbers}
            label={strings.weekNumbers}
            onChange={setWeekNumbers}
          />
          <Checkbox
            checked={shadeWeekends}
            label={strings.shadeWeekends}
            onChange={setShadeWeekends}
          />
          {layout === "months" ? (
            <>
              <Checkbox
                checked={showOutside}
                label={strings.showOutside}
                onChange={setShowOutside}
              />
              <Checkbox
                checked={notes}
                label={strings.notes}
                onChange={setNotes}
              />
            </>
          ) : null}
        </div>
        {weekNumbers ? (
          <p className="text-muted-foreground text-xs">
            {strings.weekNumbersHint}
          </p>
        ) : null}
        <TextField
          hint={strings.titleHint}
          id="pc-title"
          label={strings.titleLabel}
          onChange={setTitle}
          value={title}
        />
        <TextAreaField
          hint={strings.holidaysHint}
          id="pc-holidays"
          label={strings.holidaysLabel}
          onChange={setHolidayText}
          placeholder={strings.holidaysPlaceholder}
          prose
          rows={4}
          value={holidayText}
        />
        {parsed.errors.length > 0 ? (
          <ErrorNotice>
            {t(strings.holidayErrors, { lines: parsed.errors.join(" · ") })}
          </ErrorNotice>
        ) : null}
        <PrintBar
          fileName={`calendar-${layout}-${year}`}
          pageTotal={pages.length}
          paper={paper}
          strings={strings}
          svgRef={svgRef}
        />
      </div>
      <PrintablePages
        className={orientation === "landscape" ? "max-w-3xl" : "max-w-xl"}
        geometry={geometry}
        label={strings.previewLabel}
        pages={pages}
        svgRef={svgRef}
      />
    </div>
  );
}
